import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import StartPostButton from '../../atoms/StartPostButton';
import { ArrowRightIcon, UsersIcon } from '../../atoms/icons/EditorialProfileIcons';
import useInfiniteScrollObserver from '../../../hooks/useInfiniteScrollObserver';
import useOffsetConnectionList, { DEFAULT_CONNECTION_MODAL_PAGE_SIZE } from '../../../hooks/useOffsetConnectionList';
import {
  resolveUserConnectionAvatarUrl,
  resolveUserConnectionHandle,
  resolveUserConnectionIdentity,
  resolveUserConnectionName,
  resolveUserConnectionRole,
  resolveUserConnectionUserId,
  resolveUserConnectionUsername,
} from '../../../utils/userConnectionDisplay';
import EditorialDetailModalFrame from '../EditorialDetailModalFrame';
import EditorialEmptyState from '../EditorialEmptyState';
import EditorialFriendCard from '../EditorialFriendCard';
import './index.scss';

const EMPTY_ITEMS = Object.freeze([]);

export default function UserConnectionsModal({
  title,
  eyebrow,
  ariaLabel,
  closeLabel = 'Close',
  initialItems = EMPTY_ITEMS,
  loadPage,
  pageSize = DEFAULT_CONNECTION_MODAL_PAGE_SIZE,
  onClose,
  onSelectConnection,
  publicMedia = false,
  emptyTitle = 'No connections yet',
  emptyDescription = 'Connections appear here when available to this viewer.',
  loadingTitle = 'Loading connections...',
  errorTitle = 'Unable to load connections',
  errorDescription = 'Connections could not be loaded right now.',
  retryLabel = 'Try again',
  loadMoreLabel = 'Load more',
  loadingMoreLabel = 'Loading...',
}) {
  const {
    items,
    hasMore,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    reload,
    loadMore,
  } = useOffsetConnectionList({
    enabled: true,
    loadPage,
    initialItems,
    pageSize,
    logLabel: title,
  });

  const countLabel = useMemo(() => {
    const visibleCount = items.length;
    if (Number.isFinite(totalCount) && totalCount >= visibleCount) {
      return `${visibleCount} / ${totalCount}`;
    }
    return String(visibleCount);
  }, [items.length, totalCount]);

  const handleSelectConnection = useCallback((connection, userId) => {
    if (typeof onSelectConnection === 'function') {
      onSelectConnection(connection, userId);
      onClose();
    }
  }, [onClose, onSelectConnection]);

  const hasItems = items.length > 0;
  const showBlockingError = Boolean(error && !hasItems && !isLoading);
  const resolvedAriaLabel = ariaLabel || (typeof title === 'string' ? title : 'Connections');
  const loadMoreSentinelRef = useInfiniteScrollObserver({
    enabled: hasItems,
    hasMore,
    isLoading: isLoading || isLoadingMore,
    onLoadMore: loadMore,
  });

  return (
    <EditorialDetailModalFrame
      className="user-connections-modal"
      bodyClassName="user-connections-modal__body"
      ariaLabel={resolvedAriaLabel}
      closeLabel={closeLabel}
      onClose={onClose}
    >
      <header className="user-connections-modal__header">
        <div className="user-connections-modal__heading">
          {eyebrow ? <span className="user-connections-modal__eyebrow">{eyebrow}</span> : null}
          <h2 className="user-connections-modal__title">{title}</h2>
        </div>
        <span className="user-connections-modal__count" aria-label={`${countLabel} shown`}>
          {countLabel}
        </span>
      </header>

      {isLoading && !hasItems ? (
        <EditorialEmptyState
          icon={<UsersIcon />}
          title={loadingTitle}
          description={emptyDescription}
          className="user-connections-modal__empty"
        />
      ) : null}

      {showBlockingError ? (
        <EditorialEmptyState
          icon={<UsersIcon />}
          title={errorTitle}
          description={errorDescription}
          className="user-connections-modal__empty"
          action={(
            <StartPostButton
              label={retryLabel}
              icon={<ArrowRightIcon width="16" height="16" />}
              onClick={reload}
            />
          )}
        />
      ) : null}

      {!isLoading && !showBlockingError && !hasItems ? (
        <EditorialEmptyState
          icon={<UsersIcon />}
          title={emptyTitle}
          description={emptyDescription}
          className="user-connections-modal__empty"
        />
      ) : null}

      {hasItems ? (
        <div className="user-connections-modal__grid">
          {items.map((connection, index) => {
            const userId = resolveUserConnectionUserId(connection);
            const username = resolveUserConnectionUsername(connection);
            const key = resolveUserConnectionIdentity(connection, `connection-${index}`);
            const name = resolveUserConnectionName(connection);
            const canSelect = Boolean(userId && onSelectConnection);
            return (
              <EditorialFriendCard
                key={key}
                className="user-connections-modal__card"
                userId={userId || undefined}
                username={username || undefined}
                name={name}
                role={resolveUserConnectionRole(connection)}
                handle={resolveUserConnectionHandle(connection)}
                avatarUrl={resolveUserConnectionAvatarUrl(connection, { publicMedia })}
                publicMedia={publicMedia}
                onSelect={canSelect ? () => handleSelectConnection(connection, userId) : undefined}
              />
            );
          })}
        </div>
      ) : null}

      {error && hasItems ? (
        <p className="user-connections-modal__inline-error" role="alert">
          {errorDescription}
        </p>
      ) : null}

      {hasMore ? (
        <div className="user-connections-modal__footer">
          <StartPostButton
            label={isLoadingMore ? loadingMoreLabel : loadMoreLabel}
            icon={<ArrowRightIcon width="16" height="16" />}
            onClick={loadMore}
            disabled={isLoadingMore}
          />
        </div>
      ) : null}
      {hasMore ? <div ref={loadMoreSentinelRef} className="user-connections-modal__sentinel" aria-hidden="true" /> : null}
    </EditorialDetailModalFrame>
  );
}

UserConnectionsModal.propTypes = {
  title: PropTypes.node.isRequired,
  eyebrow: PropTypes.node,
  ariaLabel: PropTypes.string,
  closeLabel: PropTypes.string,
  initialItems: PropTypes.array,
  loadPage: PropTypes.func,
  pageSize: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSelectConnection: PropTypes.func,
  publicMedia: PropTypes.bool,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  loadingTitle: PropTypes.node,
  errorTitle: PropTypes.node,
  errorDescription: PropTypes.node,
  retryLabel: PropTypes.node,
  loadMoreLabel: PropTypes.node,
  loadingMoreLabel: PropTypes.node,
};
